import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "../api/api-client";
import { handleApiError } from "../api/error-handler";
import {
	CompanyGroup,
	CompanyGroupCreateDTO,
	CompanyGroupUpdateDTO,
} from "../models/CompanyGroup";
import { PaginatedResponse } from "../models/Pagination";
import { useState } from "react";
import { useLanguage } from "../i18n/LanguageContext";

/**
 * Hook to fetch all company groups (paginated)
 */
export const useCompanyGroups = (page = 1, pageSize = 10) => {
	return useQuery<PaginatedResponse<CompanyGroup>, Error>({
		queryKey: ["companyGroups", page, pageSize],
		queryFn: async () => {
			try {
				const response = await apiClient.get<PaginatedResponse<CompanyGroup>>(
					`/company-groups?page=${page}&pageSize=${pageSize}`
				);
				return response.data;
			} catch (error) {
				throw handleApiError(error, "Failed to fetch company groups");
			}
		},
	});
};

/**
 * Hook to search company groups by name
 */
export const useSearchCompanyGroups = () => {
	const [data, setData] = useState<PaginatedResponse<CompanyGroup> | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<Error | null>(null);
	const [isSearched, setIsSearched] = useState(false);

	const searchCompanyGroups = async (
		searchTerm: string,
		page: number = 1,
		pageSize: number = 10
	) => {
		if (!searchTerm || searchTerm.trim().length < 2) {
			return;
		}

		setIsLoading(true);
		setError(null);
		setIsSearched(true);

		try {
			const url = `/company-groups/search?searchTerm=${encodeURIComponent(searchTerm.trim())}&page=${page}&pageSize=${pageSize}`;
			const response = await apiClient.get<PaginatedResponse<CompanyGroup>>(url);
			setData(response.data);
		} catch (error) {
			setError(handleApiError(error, "Failed to search company groups"));
		} finally {
			setIsLoading(false);
		}
	};

	const resetSearch = () => {
		setIsSearched(false);
		setData(null);
		setError(null);
	};

	return { data, isLoading, error, isSearched, searchCompanyGroups, resetSearch };
};

/**
 * Hook to fetch a specific company group by ID
 */
export const useCompanyGroup = (id: number) => {
	return useQuery<CompanyGroup, Error>({
		queryKey: ["companyGroup", id],
		queryFn: async () => {
			try {
				const response = await apiClient.get(`/company-groups/${id}`);
				return response.data;
			} catch (error) {
				throw handleApiError(error, `Failed to fetch company group with ID ${id}`);
			}
		},
		enabled: id !== 0,
	});
};

/**
 * Hook to create a new company group
 */
export const useCreateCompanyGroup = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (data: CompanyGroupCreateDTO) => {
			try {
				const response = await apiClient.post("/company-groups", data);
				return response.data;
			} catch (error) {
				throw handleApiError(error, "Failed to create company group");
			}
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["companyGroups"] });
		},
	});
};

/**
 * Hook to update a company group
 */
export const useUpdateCompanyGroup = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ id, data }: { id: number; data: CompanyGroupUpdateDTO }) => {
			try {
				const response = await apiClient.put(`/company-groups/${id}`, data);
				return response.data;
			} catch (error) {
				throw handleApiError(error, "Failed to update company group");
			}
		},
		onSuccess: (_, { id }) => {
			queryClient.invalidateQueries({ queryKey: ["companyGroup", id] });
			queryClient.invalidateQueries({ queryKey: ["companyGroups"] });
		},
	});
};

/**
 * Hook to search company groups for autocomplete
 */
export const useSearchCompanyGroupsAutocomplete = (searchTerm: string, minChars: number = 2) => {
	const { currentLanguage } = useLanguage();

	return useQuery<CompanyGroup[], Error>({
		queryKey: ["companyGroups", "search", searchTerm, currentLanguage],
		queryFn: async () => {
			try {
				const response = await apiClient.get<PaginatedResponse<CompanyGroup>>(
					`/company-groups/search?searchTerm=${encodeURIComponent(searchTerm)}&pageSize=100`
				);
				return response.data.items ?? [];
			} catch (error) {
				throw handleApiError(error, "Failed to search company groups");
			}
		},
		enabled: searchTerm.length >= minChars,
		staleTime: 10000,
	});
};

/**
 * Hook to remove a company from a group
 */
export const useRemoveCompanyFromGroup = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ groupId, companyId }: { groupId: number; companyId: number }) => {
			try {
				const response = await apiClient.delete(
					`/company-groups/${groupId}/companies/${companyId}`
				);
				return response.data;
			} catch (error) {
				throw handleApiError(error, "Failed to remove company from group");
			}
		},
		onSuccess: (_, { groupId }) => {
			queryClient.invalidateQueries({ queryKey: ["companyGroup", groupId] });
			queryClient.invalidateQueries({ queryKey: ["companyGroups"] });
		},
	});
};
